import { DateTime } from "luxon";
import moment from "moment";

const TIME_FORMAT = "HH:mm";

// Accepted server date formats. The server sometimes returns timestamps with
// single-digit seconds ("2026-06-04 08:24:0"), no seconds ("2026-06-04 08:22")
// or a trailing colon ("2026-06-04 08:24:"). moment parses these reliably only
// when given an explicit (non-strict) format list, so list every variant here.
const SERVER_DATE_FORMATS = [
  "YYYY-MM-DD HH:mm:ss.S",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss.S",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
];

// Parse a server date string into a moment in UTC, tolerating the malformed
// (truncated-seconds) timestamps the backend occasionally sends.
const parseServerDate = (date) => {
  if (!date || typeof date !== "string") return null;
  // Drop any trailing colon ("08:24:") so the no-seconds format can match.
  const cleaned = date.trim().replace(/:$/, "");
  return moment.utc(cleaned, SERVER_DATE_FORMATS);
};

/**
 * formatAMPM() method to perform the am or pm format with time.
 *
 * @param {date} date
 */
function formatAMPM(date) {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  let ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12;
  hours =  hours || 12;
  minutes = minutes < 10 ? "0" + minutes : minutes;
  return hours + ":" + minutes + " " + ampm;
}

/**
 * formatChatDate() method to perform convert into Today, Yesterday and Date format.
 *
 * @param {date} date
 */
export const formatChatDate = (date) => {
  const dateCurrent = DateTime.fromMillis(Date.now());
  const dateMessage = DateTime.fromJSDate(date);
  const datesDiff = dateCurrent.diff(dateMessage, "days").toObject().days;
  if (!datesDiff) {
    return null; /** change ? to null */
  }
  const diffRounded = Math.round(datesDiff);

  if (diffRounded === 0) {
    return "Today";
  } else if (diffRounded === 1) {
    return "Yesterday";
  } else if (diffRounded > 1 && diffRounded <= 6) {
    return DateTime.fromJSDate(date).toFormat("cccc");
  } else {
    const isCurrentYear = date.getFullYear() === new Date().getFullYear()
    return DateTime.fromJSDate(date).toFormat(isCurrentYear ? "d MMM" : "d MMM yyyy");
  }
};

/**
 * formatChatTime() method to perform convert time into now, min ago, mins ago and date format.
 *
 * @param {date} date
 * @param {string} type
 */
export const formatChatTime = (date, type) => {
  if (type === "recent-chat") {
    return formatAMPM(date);
  }
  const dateCurrent = DateTime.fromMillis(Date.now());
  const dateMessage = DateTime.fromJSDate(date);
  const datesDiff = dateCurrent.diff(dateMessage, "minutes").toObject().minutes;

  if (!datesDiff) {
    return null; /** change ? to null */
  }

  const diffRounded = Math.round(datesDiff);

  if (diffRounded === 0) {
    return "Now";
  } else if (diffRounded === 1) {
    return `${diffRounded} min ago`;
  } else if (diffRounded < 60) {
    return `${diffRounded} mins ago`;
  } else {
    return dateMessage.toFormat(TIME_FORMAT);
  }
};

/**
 * formatChatDateTime() method to perform the format date & time.
 *
 * @param {Date} date
 * @param {string} type
 */
export const formatChatDateTime = (date, type) => {
  const timeFormatted = formatChatTime(date, type);
  const dayFormatted = formatChatDate(date);

  if (type === "recent-chat") {
    if (dayFormatted === "Today") {
      return timeFormatted;
    }
    return dayFormatted;
  } else {
    if (timeFormatted.match(/Now|mins? ago/i)) {
      return timeFormatted;
    }
    return dayFormatted;
  }
};

/**
 * convertUTCTOLocalTimeStamp() method to perform convert UTC to local time formate.
 *
 * @param {date} date
 */
export const convertUTCTOLocalTimeStamp = (date) => {
  date = new Date(date.replace(/-/g, "/"));
  let newDate = new Date(date);
  newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset());
  return newDate;
};

function datetoTime(secs) {
  let todayDate = new Date();
  todayDate.setSeconds(todayDate.getSeconds() - secs);
  return todayDate;
}

function secondsToHms(secs) {
  if (secs === 0){
    return 0;
  }
  secs = Number(secs);
  let calcHours = Math.floor(secs / 3600);
  return calcHours > 0 ? calcHours : 0;
}

function findYesterday(secs , currentDate){
  let currentDateInSec = currentDate.getUTCHours();
  let remaingSec =  secondsToHms(secs) - currentDateInSec;  
  return remaingSec  <= 24;
}

export const getLastseen = (secs) => {
  let userDate = datetoTime(secs);
  let currentDate = new Date();
  const weekday = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const HHMM = { hour: "numeric", minute: "numeric" };
  if (secs === 0) {
    return "Online";
  } else if (userDate.getDate() === currentDate.getDate() && userDate.getMonth() === currentDate.getMonth()) {
    return `last seen today at ${userDate.toLocaleTimeString("en-US", HHMM)}`;
  } else if ((userDate.getDate() === currentDate.getDate() - 1 && userDate.getMonth() === currentDate.getMonth()) || (userDate.getMonth() === (currentDate.getMonth() -1) && findYesterday(secs ,currentDate))) {
    return `last seen yesterday at ${userDate.toLocaleTimeString("en-US", HHMM)}`;
  } else if (
    (userDate.getDate() === currentDate.getDate() - 1 ||
      userDate.getDate() === currentDate.getDate() - 2 ||
      userDate.getDate() === currentDate.getDate() - 3 ||
      userDate.getDate() === currentDate.getDate() - 4 ||
      userDate.getDate() === currentDate.getDate() - 5 ||
      userDate.getDate() === currentDate.getDate() - 6) &&
    userDate.getMonth() === currentDate.getMonth()
  ) {
    return `last seen on ${weekday[userDate.getDay()]} at ${userDate.toLocaleTimeString("en-US", HHMM)}`;
  } else if (userDate.getDate().toString().length > 1) {
      return `last seen ${userDate.getDate()}-${month[userDate.getMonth()]}-${userDate.getFullYear()}`;
  } else {
      return `last seen ${0}${userDate.getDate()}-${month[userDate.getMonth()]}-${userDate.getFullYear()}`;
  }
};

export const convertUTCDateToLocalDate = (date) => {
  const parsed = parseServerDate(date);

  if (!parsed || !parsed.isValid()) {
    console.warn("Invalid createdAt:", date);
    return "Invalid Date";
  }

  return parsed.local().format("YYYY-MM-DD");
};

export const convertLocalDateForStarredMessage = (date) => {
  const parsed = parseServerDate(date);

  if (!parsed || !parsed.isValid()) {
    console.warn("Invalid createdAt:", date);
    return "Invalid Date";
  }

  return parsed.local().format("DD MMM YYYY");
};

